import React, { useEffect, useState } from "react";
import axios from "axios";
import { referralApi } from "../services/phase1Api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Share2, Loader2, CheckCircle2, Send, History, Users } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  "Referral Sent": "bg-slate-100 text-slate-600",
  Visited: "bg-blue-100 text-blue-700",
  Registered: "bg-indigo-100 text-indigo-700",
  "Pilot Active": "bg-amber-100 text-amber-700",
  Customer: "bg-emerald-100 text-emerald-700",
};

// Resolve ctx headers: use explicit school headers, else derive from the
// session cookie via /api/auth/me (portfolio/sales/session portals).
async function resolveHeaders(authHeaders) {
  if (authHeaders) return authHeaders;
  try {
    const { data: me } = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
    return {
      "x-user-id": me.id || me.user_id || "public",
      "x-user-role": me.role || "client",
      "x-tenant-id": me.tenant_id || "tenant_1",
    };
  } catch {
    return { "x-user-id": "public", "x-user-role": "client", "x-tenant-id": "tenant_1" };
  }
}

export function ShareDialog({ open, onOpenChange, authHeaders }) {
  const [tab, setTab] = useState("invite");
  const [headers, setHeaders] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [form, setForm] = useState({ recipient_name: "", recipient_email: "", recipient_org: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [history, setHistory] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTab("invite"); setDone(null);
    (async () => {
      const h = await resolveHeaders(authHeaders);
      setHeaders(h);
      referralApi.context(h).then(setCtx).catch(() => setCtx(null));
    })();
  }, [open, authHeaders]);

  useEffect(() => {
    if (tab === "history" && headers) {
      setHistory(null);
      referralApi.mine(headers).then(setHistory).catch(() => setHistory({ referrals: [], counts: {} }));
    }
  }, [tab, headers]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canSend = form.recipient_name.trim() && /\S+@\S+\.\S+/.test(form.recipient_email) && (ctx?.remaining_today ?? 1) > 0;

  const submit = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const res = await referralApi.send(form, headers);
      if (res.duplicate) {
        toast.info(res.message);
        setDone({ duplicate: true, message: res.message });
      } else {
        toast.success("Invitation sent!");
        setDone({ duplicate: false });
        setForm({ recipient_name: "", recipient_email: "", recipient_org: "", message: "" });
        referralApi.context(headers).then(setCtx).catch(() => {});
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send invitation");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Share2 className="h-4 w-4 text-primary" /> Invite an organization</DialogTitle>
          <DialogDescription>
            Know another school or organization that would benefit from MySchoolRx? Send them a personal invite.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 border-b border-border mb-1">
          <button onClick={() => setTab("invite")} data-testid="share-tab-invite"
            className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "invite" ? "border-primary text-foreground" : "border-transparent text-foreground/50"}`}>
            <Send className="h-3.5 w-3.5 inline mr-1.5" /> Invite
          </button>
          <button onClick={() => setTab("history")} data-testid="share-tab-history"
            className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "history" ? "border-primary text-foreground" : "border-transparent text-foreground/50"}`}>
            <History className="h-3.5 w-3.5 inline mr-1.5" /> My referrals
          </button>
        </div>

        {tab === "invite" ? (
          done && !done.duplicate ? (
            <div className="py-6 text-center" data-testid="share-success">
              <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
              <p className="text-sm font-medium text-foreground">Invitation sent!</p>
              <p className="text-xs text-foreground/50 mt-1">We'll keep you posted as they engage.</p>
              <Button size="sm" variant="outline" className="mt-4" onClick={() => setDone(null)} data-testid="share-send-another">Send another</Button>
            </div>
          ) : (
            <div className="space-y-3">
              {done?.duplicate && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-800" data-testid="share-duplicate">
                  {done.message} Our team has been notified.
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-semibold text-foreground/50 block mb-1">Recipient name</label>
                  <Input data-testid="share-name" value={form.recipient_name} onChange={(e) => set("recipient_name", e.target.value)} placeholder="Jane Principal" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-semibold text-foreground/50 block mb-1">Recipient email</label>
                  <Input data-testid="share-email" type="email" value={form.recipient_email} onChange={(e) => set("recipient_email", e.target.value)} placeholder="jane@school.org" />
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider font-semibold text-foreground/50 block mb-1">Organization / school name</label>
                <Input data-testid="share-org" value={form.recipient_org} onChange={(e) => set("recipient_org", e.target.value)} placeholder="Bright Futures Academy" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider font-semibold text-foreground/50 block mb-1">Personal message (optional)</label>
                <Textarea data-testid="share-message" rows={2} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Thought your team would find this valuable…" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-foreground/40" data-testid="share-remaining">
                  {ctx ? `${ctx.remaining_today} of ${ctx.daily_limit} invites left today` : ""}
                </span>
                <Button onClick={submit} disabled={!canSend || sending} data-testid="share-send-btn">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />} Send invite
                </Button>
              </div>
            </div>
          )
        ) : (
          <div data-testid="share-history">
            {!history ? (
              <div className="py-6 flex items-center justify-center text-foreground/40"><Loader2 className="h-4 w-4 animate-spin" /></div>
            ) : history.referrals.length === 0 ? (
              <div className="py-8 text-center text-sm text-foreground/40"><Users className="h-6 w-6 mx-auto mb-2 opacity-40" />No referrals sent yet.</div>
            ) : (
              <div className="max-h-72 overflow-auto -mx-1">
                {history.referrals.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-2 px-1 py-2 border-b border-border/40 text-sm">
                    <div className="min-w-0">
                      <div className="font-medium text-foreground truncate">{r.recipient_org || r.recipient_name}</div>
                      <div className="text-[11px] text-foreground/40 truncate">{r.recipient_email}</div>
                    </div>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium shrink-0 ${STATUS_COLORS[r.status] || "bg-slate-100"}`}>{r.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ShareButton({ authHeaders, variant = "outline", size = "sm", className = "", label = "Share" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant={variant} size={size} className={className} onClick={() => setOpen(true)} data-testid="share-button">
        <Share2 className="h-4 w-4 mr-1.5" /> {label}
      </Button>
      <ShareDialog open={open} onOpenChange={setOpen} authHeaders={authHeaders} />
    </>
  );
}

export default ShareButton;
